import { NavLink, useNavigate } from "react-router-dom";
import { Camera, Clock, Server, Download, Users, LogOut, Maximize, Settings } from "lucide-react";
import clsx from "clsx";

import { Button } from "../common/Button";
import { AppLogo } from "../common/AppLogo";
import { useAuthStore } from "../../store/authStore";
import { useWsStore } from "../../store/wsStore";
import { hasPermission } from "../../lib/permissions";
import type { Permission } from "../../types";
import { clearClientSession } from "../../store/session";

const NAV: { to: string; label: string; icon: typeof Camera; perm?: Permission; adminOnly?: boolean }[] = [
  { to: "/live", label: "Vivo", icon: Camera, perm: "can_view_live" },
  { to: "/playback", label: "Playback", icon: Clock, perm: "can_view_recordings" },
  { to: "/devices", label: "Dispositivos", icon: Server, perm: "can_manage_devices" },
  { to: "/downloads", label: "Descargas", icon: Download, perm: "can_download_recordings" },
  { to: "/users", label: "Usuarios", icon: Users, perm: "can_manage_users" },
  { to: "/settings", label: "Configuración", icon: Settings, adminOnly: true },
];

export function TopBar() {
  const user = useAuthStore((s) => s.user);
  const wsConnected = useWsStore((s) => s.connected);
  const nav = useNavigate();

  const logout = () => {
    clearClientSession();
    nav("/login", { replace: true });
  };

  const goFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  const visibleNav = NAV.filter((item) => {
    if (item.adminOnly && user?.role !== "admin") return false;
    if (item.perm && !hasPermission(user, item.perm)) return false;
    return true;
  });

  return (
    <header className="flex items-center justify-between bg-panel border-b border-line px-3 py-1.5 text-sm no-select">
      <div className="flex items-center gap-3">
        <AppLogo size="sm" />
        <nav className="flex items-center gap-1">
          {visibleNav.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                clsx(
                  "flex items-center gap-1 px-2 py-1 rounded text-xs",
                  isActive ? "bg-accent text-white" : "text-text hover:bg-line",
                )
              }
            >
              <Icon size={14} />
              {label}
            </NavLink>
          ))}
        </nav>
      </div>
      <div className="flex items-center gap-2">
        <span
          className={clsx(
            "inline-block w-2 h-2 rounded-full",
            wsConnected ? "bg-green-500" : "bg-red-500",
          )}
          title={wsConnected ? "Eventos conectados" : "Eventos desconectados"}
        />
        <span className="text-muted text-xs">
          {user?.username} <span className="opacity-50">({user?.role})</span>
        </span>
        <Button variant="ghost" size="sm" onClick={goFullscreen} title="Pantalla completa">
          <Maximize size={14} />
        </Button>
        <Button variant="ghost" size="sm" onClick={logout} title="Cerrar sesion">
          <LogOut size={14} />
        </Button>
      </div>
    </header>
  );
}
